import React, { useState, useEffect, useCallback } from 'react';
import InactivityDialog from './InactivityDialog';
import { refreshpostApiAsyn } from '../../services/PostAPI';

const WARNING_MINUTES_BEFORE = 15;
const CHECK_INTERVAL_MS = 60000;

const decodeToken = (token) => {
  try {
    if (!token) return null;
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

const TokenExpirationHandler = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const token = localStorage.getItem('token');
  const isLoggedIn = token && token !== '' && token !== 'null' && token !== 'undefined';

  const getTimeUntilExpiry = useCallback(() => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return null;

    const decoded = decodeToken(currentToken);
    if (!decoded || !decoded.exp) return null;

    const expiryTimestamp = decoded.exp * 1000;
    const now = Date.now();
    
    return Math.max(0, Math.floor((expiryTimestamp - now) / 1000));
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    window.location.replace('/');
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('token');
      
      // Check if token still exists before attempting refresh
      if (!currentToken || currentToken === 'null' || currentToken === 'undefined') {
        console.error('No valid token found, logging out');
        handleLogout();
        return;
      }
      
      const response = await refreshpostApiAsyn('auth/refreshToken', currentToken);
      
      // Check if refresh failed (401 or error response)
      if (response?.APIResult === 0 || !response?.token) {
        console.error('Token refresh failed:', response?.Message || 'No token received');
        handleLogout();
        return;
      }
      
      const newToken = response?.token;
      
      if (newToken) {
        localStorage.removeItem('token');
        localStorage.setItem('token', newToken);
        setShowWarning(false);
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      handleLogout();
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const checkExpiration = () => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return;

      const secondsUntilExpiry = getTimeUntilExpiry();
      
      if (secondsUntilExpiry === null) {
        return;
      }

      if (secondsUntilExpiry <= 0) {
        handleLogout();
        return;
      }

      const warningThreshold = WARNING_MINUTES_BEFORE * 60;
      if (secondsUntilExpiry <= warningThreshold) {
        setShowWarning(true);
        setRemainingTime(secondsUntilExpiry);
      }
    };

    checkExpiration();

    const intervalId = setInterval(checkExpiration, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isLoggedIn, getTimeUntilExpiry, handleLogout]);

  useEffect(() => {
    if (!showWarning) return;

    const countdownId = setInterval(() => {
      const secondsUntilExpiry = getTimeUntilExpiry();
      
      if (secondsUntilExpiry === null || secondsUntilExpiry <= 0) {
        handleLogout();
        return;
      }
      
      setRemainingTime(secondsUntilExpiry);
    }, 1000);

    return () => clearInterval(countdownId);
  }, [showWarning, getTimeUntilExpiry, handleLogout]);

  if (!isLoggedIn) {
    return null;
  }

  return (
    <InactivityDialog
      open={showWarning}
      remainingTime={remainingTime}
      onContinue={handleRefresh}
      onLogout={handleLogout}
    />
  );
};

export default TokenExpirationHandler;
